import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiAdmin, getApiErrorMessage, getSplashData } from "../../services/api";
import { Language } from "../../App";
import { IoClose, IoCheckmarkCircle, IoMoonOutline, IoInformationCircleOutline } from "react-icons/io5";
import { HiOutlineDocumentText } from "react-icons/hi2";
import { CiCircleCheck } from "react-icons/ci";
import { GoCheckCircle } from "react-icons/go";
import { BsMoon } from "react-icons/bs";
import { HiOutlineMoon } from "react-icons/hi";
import { PiFilePdf } from "react-icons/pi";
import { IoIosCheckmarkCircleOutline, IoMdInformationCircleOutline } from "react-icons/io";
import { FaRegCircleCheck } from "react-icons/fa6";

function RadioOptionRow({ icon, title, sub, selected, activeColor,activeColorSub, activeColorBg, activeColorIcon, activeBg, activeBorder, onClick }) {
  return (
    <div
      onClick={onClick}
      className="d-flex align-items-center justify-content-between mb-2"
      style={{
        border: `1px solid ${selected ? activeBorder : "#E6E7EA"}`,
        background: selected ? activeBg : "#fff",
        borderRadius: "12px",
        padding: "8px 14px",
        cursor: "pointer",
      }}
    >
      <div className="d-flex align-items-center gap-3">
        <span
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ backgroundColor:selected ? activeColorBg : "#F9FAFA", border: `1px solid ${selected ? "transparent" : "#D7E0EA"}`, color: selected ? activeColorIcon : "#98A2B3", borderRadius:"10px", padding:"7px" }}
        >
          {icon}
        </span>
        <div>
          <span
            className="fs-14 d-block"
            style={{ color: selected ? activeColor : "#031316" , fontWeight:"500"}}
          >
            {title}
          </span>
          <span className="fs-12 d-block" style={{ color: selected ? activeColorSub: "#5F6673" , fontWeight:"400" }}>
            {sub}
          </span>
        </div>
      </div>
      <span
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          border: `1.5px solid ${selected ? activeColor : "#6D7683"}`,
        }}
      >
        {selected && (
          <span
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: activeColor,
            }}
          />
        )}
      </span>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className=" flex-shrink-0"
      style={{
        width: "40px",
        height: "22px",
        borderRadius: "999px",
        background: checked ? "#16A34A" : "#94A3B8",
        padding: "1px",
       border: `0.5px solid ${checked ? "transparent" : "#E5E7EB"}`,
        cursor: "pointer",
        transition: "background 0.15s ease",
      }}
    >
      <span
        style={{
          display: "block",
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          background: "#fff",
          transform: checked ? "translateX(18px)" : "translateX(0)",
          transition: "transform 0.15s ease",
        }}
      />
    </button>
  );
}

// Same mentor_profile the login response carries (Login.jsx saves the whole
// response under "SignupData"; SplashData gets the same user object synced
// in right after, and stays fresher afterwards since other flows — e.g.
// CreateMentorship.jsx's own submit — refresh SplashData but never touch
// SignupData again) — SplashData is preferred when present, SignupData is
// the fallback for the rare case it hasn't loaded yet.
function getMentorProfileSettings() {
  try {
    const splashProfile = JSON.parse(localStorage.getItem("SplashData"))?.user?.mentor_profile;
    const signupProfile = JSON.parse(localStorage.getItem("SignupData"))?.data?.mentor_profile;
    const profile = splashProfile || signupProfile;
    if (!profile) return null;
    return {
      isReceiveData: profile.is_receive_data === 1,
      receiveDataTitle: profile.receive_data_title || "",
      // Mirrors the `available` key handleSave() posts back — 0 is only
      // ever "unavailable" when the field is actually present; missing
      // (undefined, e.g. never saved before) keeps the "available" default.
      availability: profile.available === 0 ? "unavailable" : "available",
    };
  } catch (e) {
    return null;
  }
}

function SectionCard({ title, sub, children }) {
  return (
    <div
      className="mb-3"
      style={{ border: "1px solid #E2E8F0", borderRadius: "20px", padding: "16px" }}
    >
      <p className="fs-14  mb-0" style={{ color: "#0F172A", fontWeight:"700" }}>
        {title}
      </p>
      <p className="fs-12 mb-3" style={{ color: "#848798", fontWeight:"400" }}>
        {sub}
      </p>
      {children}
    </div>
  );
}

// Shared shape used both to seed state and as the "last saved" baseline we
// diff against for the Save button's disabled state.
function buildSettingsState(settings, lang) {
  return {
    availability: settings?.availability ?? "available",
    // Prefer the real server value (mentor_profile.is_receive_data) over the
    // old mr_receive_cv_enabled localStorage flag, which only ever reflected
    // whatever was last saved from THIS modal and could go stale — e.g. if
    // the setting were ever changed from somewhere else, or on a fresh
    // browser that never had this modal open before but does have a real
    // saved mentor profile.
    receiveCv: settings?.isReceiveData ?? (localStorage.getItem("mr_receive_cv_enabled") === "true"),
    // Shows the mentor's own saved title when CV-receiving is actually on
    // and they've set one; otherwise falls back to the default placeholder
    // wording — editable either way.
    cvSectionTitle:
      settings?.isReceiveData && settings.receiveDataTitle
        ? settings.receiveDataTitle
        : lang?.mentorship_upload_your_cv_default || "UPLOAD YOUR CV",
  };
}

function MentorSetting({ isOpen, toggle }) {
  const { lang } = useContext(Language);
  const mentorProfileSettings = getMentorProfileSettings();
  const [availability, setAvailability] = useState(() => buildSettingsState(mentorProfileSettings, lang).availability);
  const [receiveCv, setReceiveCv] = useState(() => buildSettingsState(mentorProfileSettings, lang).receiveCv);
  const [cvSectionTitle, setCvSectionTitle] = useState(() => buildSettingsState(mentorProfileSettings, lang).cvSectionTitle);
  const [saving, setSaving] = useState(false);
  // The last-saved values, snapshotted whenever the modal (re)opens — the
  // Save button stays disabled until the current state actually diverges
  // from this baseline, so it only becomes clickable once the user has
  // really changed something.
  const [baseline, setBaseline] = useState(() => buildSettingsState(mentorProfileSettings, lang));

  // This modal never actually unmounts between opens (see the `if (!isOpen
  // && !isClosing) return null` below — it just stops rendering), so the
  // useState initializers above only ever run once on first mount. Without
  // this, toggling "Receive CV"/picking "Not available" and then closing
  // without saving left those choices sitting in state — reopening showed
  // the unsaved edit instead of what was actually last saved. Re-reading
  // getMentorProfileSettings() here and resetting on every open discards
  // any unsaved edit and restores the real saved values, exactly like
  // CreateMentorship.jsx's own isOpen-keyed reset effect does for its form.
  useEffect(() => {
    if (!isOpen) return;
    const settings = buildSettingsState(getMentorProfileSettings(), lang);
    setAvailability(settings.availability);
    setReceiveCv(settings.receiveCv);
    setCvSectionTitle(settings.cvSectionTitle);
    setBaseline(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hasChanges =
    availability !== baseline.availability ||
    receiveCv !== baseline.receiveCv ||
    (receiveCv && cvSectionTitle !== baseline.cvSectionTitle);

  // Same curtain-modal pattern as ApplyMentorShip.jsx / NewCourseDetail2.0.jsx's
  // VideoModal — plain backdrop + content divs animated with @keyframes,
  // not reactstrap's Modal/Fade, so open and close are both immediate and
  // symmetric instead of gated behind a JS timeout.
  const [isClosing, setIsClosing] = useState(false);
  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    // Kept only as the fallback getMentorProfileSettings() above reads when
    // SplashData/SignupData haven't loaded a mentor_profile yet — the real
    // source of truth once they have is the server's own is_receive_data.
    localStorage.setItem("mr_receive_cv_enabled", String(receiveCv));

    setSaving(true);
    try {
      const res = await apiAdmin.post("user/mentor/update", {
        available: availability === "available" ? 1 : 0,
        is_receive_data: receiveCv ? 1 : 0,
        receive_data_title: cvSectionTitle,
      });
      if (res.data?.status) {
        toast.success(lang?.mentorship_settings_saved_toast || "Your mentor settings have been saved.");
        // Refresh SplashData right away so getMentorProfileSettings() reads
        // the just-saved values on the next open — without this, the
        // isOpen-keyed reset effect above would re-read the stale
        // pre-save mentor_profile and wipe out what was just saved.
        try {
          const splashRes = await getSplashData();
          if (splashRes.data?.status) {
            localStorage.setItem("SplashData", JSON.stringify(splashRes.data));
          }
        } catch (splashErr) {
          // Non-fatal — the next natural splash refetch will pick it up.
        }
        // These values are now what's actually saved — move the baseline up
        // so the Save button goes back to disabled instead of staying
        // enabled from a diff against the old, pre-save values.
        setBaseline({ availability, receiveCv, cvSectionTitle });
        closeThen();
      } else {
        toast.error(getApiErrorMessage(res.data, lang?.mentorship_error_save_settings || "Couldn't save your mentor settings."));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data, lang?.mentorship_error_save_settings || "Couldn't save your mentor settings."));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`ms-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        /* Same curtain-modal pattern as ApplyMentorShip.jsx — plain
           backdrop + content divs animated with @keyframes, driven by our
           own isClosing state instead of reactstrap's Modal/Fade, so open
           and close are both immediate and symmetric. */
        .ms-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
          animation: msBackdropFadeIn 0.8s ease-out forwards;
        }
        .ms-curtain-backdrop.closing {
          animation: msBackdropFadeOut 0.8s ease-out forwards;
        }
        .ms-curtain-content {
          position: relative;
          width: 100%;
          max-width: 539px;
          margin: auto;
          background: #fff;
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15);
          transform: translateY(-100vh);
          animation: msCurtainOpen 0.8s ease-out forwards;
        }
        .ms-curtain-content.closing {
          animation: msCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes msBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes msBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes msCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes msCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ms-curtain-backdrop, .ms-curtain-content {
            animation: none !important;
          }
        }
      `}</style>
      <div
        className={`ms-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
      <div className="position-relative" style={{ padding: "28px" }}>
        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: "16px",
            right: "16px",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #F7F7F7",
            boxShadow: "0 2px 8px rgba(28, 37, 51, 0.16)",
          }}
        >
          <IoClose size={20} color="#0E1421" />
        </button>

        {/* Header */}
        <h5 className=" fs-20 mb-1" style={{ color: "#031316", fontWeight:"700" }}>
          {lang?.mentorship_settings_title || "Mentor Settings"}
        </h5>
        <p className="fs-12  mb-4" style={{ color: "#5F6673", fontWeight:"500" }}>
          {lang?.mentorship_settings_subtitle || "Manage your availability and mentorship preferences."}
        </p>

        {/* Set availability */}
        <SectionCard
          title={lang?.mentorship_set_availability_title || "Set availability"}
          sub={lang?.mentorship_set_availability_sub || "Control when you're open for new sessions."}
        >
          <RadioOptionRow
            icon={<GoCheckCircle size={17} />}
            title={lang?.mentorship_available_title || "Available"}
            sub={lang?.mentorship_available_sub || "Open to new mentee sessions"}
            selected={availability === "available"}
            activeColor="#15803D"
            activeColorSub ="#15803DCC"
            activeColorBg ="#15803D1A"
            activeBg="#15803D0A"
            activeBorder="#15803D"
            activeColorIcon="#15803D"
            onClick={() => setAvailability("available")}
          />
          <RadioOptionRow
            icon={<HiOutlineMoon size={16} />}
            title={lang?.mentorship_not_available_title || "Not available"}
            sub={lang?.mentorship_not_available_sub || "On leave or fully booked"}
            selected={availability === "unavailable"}
            activeColor="#F86500"
            activeColorSub= "#F86500CC"
             activeColorBg ="#F865001A"
               activeColorIcon="#F86500"
            activeBg="#F865000A"
            activeBorder="#F86500"
            onClick={() => setAvailability("unavailable")}
          />
        </SectionCard>

        {/* Application Settings */}
        <SectionCard
          title={lang?.mentorship_app_settings_title || "Application Settings"}
          sub={lang?.mentorship_app_settings_sub || "Manage how mentees submit mentorship requests."}
        >
          <div
            className="d-flex align-items-start justify-content-between gap-2 mb-3"
            style={{ border: "1px solid #E6E7EA", borderRadius: "20px", padding: "12px 14px" }}
          >
            <div className="d-flex align-items-start gap-2" style={{ minWidth: 0 }}>
              <span
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ color: "#00A9C7", marginTop: "2px", padding:"7px", borderRadius:"10px", backgroundColor:"#E8FBFF" }}
              >
                <PiFilePdf size={18} />
              </span>
              <div>
                <span className="fs-12 d-block" style={{ color: "#031316", fontWeight:"500" }}>
                  {lang?.mentorship_receive_cv_title || "Receive CV with Mentorship Requests"}
                </span>
                <span
                  className="fs-10 d-block"
                  style={{ color: "#848798", fontWeight:"400", lineHeight: "15px" }}
                >
                  {lang?.mentorship_receive_cv_desc || "Require mentees to upload their CV when submitting a mentorship request. This helps you review their professional background before accepting the request."}
                </span>
              </div>
            </div>
            <ToggleSwitch checked={receiveCv} onChange={setReceiveCv} />
          </div>

          {receiveCv && (
            <div className="mb-3">
              <span className="fs-12 d-block mb-2" style={{ color: "#0E1421", fontWeight:"500" }}>
                {lang?.section_title || "Section Title"}
              </span>
              <input
                type="text"
                className="roboto-regular"
                value={cvSectionTitle}
                onChange={(e) => setCvSectionTitle(e.target.value)}
                maxLength={20}
                style={{
                  width: "100%",
                  border: "1px solid #E6E7EA",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "13px",
                  color: "#0E1421",
                  outline: "none",
                }}
              />
            </div>
          )}

          <div
            className="d-flex align-items-start gap-2"
            style={{ background: "#FFF7E9", border: "1px solid #E6E7EA", borderRadius: "12px", padding: "8px 14px" }}
          >
            <span
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ color: "#6D7683", marginTop:"6px" }}
            >
           <IoMdInformationCircleOutline size={18} />
            </span>
            <p className="fs-12 mb-0" style={{ color: "#5F6673", lineHeight: "16px", fontWeight:"400" }}>
              {lang?.mentorship_cv_info_desc || "When enabled, mentees must upload their CV while submitting a mentorship request."}
            </p>
          </div>
        </SectionCard>

        {/* Save Changes */}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="w-100 d-flex align-items-center justify-content-center gap-2 border-0 text-white"
          style={{
            background: "#1DC1DD",
            borderRadius: "999px",
            padding: "12px",
            fontSize: "15px",
            fontWeight: "700",
            opacity: saving || !hasChanges ? 0.5 : 1,
            cursor: saving || !hasChanges ? "not-allowed" : "pointer",
          }}
        >
      <FaRegCircleCheck size={17} /> {saving ? lang?.saving || "Saving…" : lang?.save_changes || "Save Changes"}
        </button>
      </div>
      </div>
    </div>
  );
}

export default React.memo(MentorSetting);
